import {FC} from 'react'

import {Spinner} from '@/components/Spinner'
import {MISSIVE_BUTTON_OFFSET, MISSIVE_BUTTON_SIZE} from '@/theme/sizing'
import {theme} from '@/theme/theme'

import {MissiveChatIcon} from './MissiveChatIcon'

type MissiveChatLauncherProps = {
  loading: boolean
  onOpen: () => void
  // Fired on the first sign the visitor is heading for the button, so the widget can start
  // downloading before the click lands and most opens need no visible wait at all.
  onIntent: () => void
}

/**
 * Stand-in for the Missive chat button, shown until the widget's own button takes over - which is
 * not the same as the widget having loaded, since a preload leaves the launcher in place. Same size
 * and position as `#missive-webchat`
 */
export const MissiveChatLauncher: FC<MissiveChatLauncherProps> = ({loading, onOpen, onIntent}) => (
  <>
    <button
      id="missive-chat-launcher"
      type="button"
      aria-label="Chat with us"
      aria-busy={loading}
      onClick={onOpen}
      onPointerEnter={onIntent}
      onTouchStart={onIntent}
      onFocus={onIntent}
    >
      {loading ? (
        <Spinner color={theme.palette.text.white} />
      ) : (
        <MissiveChatIcon color={theme.palette.text.white} />
      )}
    </button>

    <style jsx>{`
      button {
        position: fixed;
        right: ${MISSIVE_BUTTON_OFFSET};
        bottom: ${MISSIVE_BUTTON_OFFSET};
        z-index: 10;
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${MISSIVE_BUTTON_SIZE};
        height: ${MISSIVE_BUTTON_SIZE};
        border-radius: 50%;
        background-color: ${theme.palette.primary.main};
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        cursor: pointer;
        transition: background-color 0.2s ease-in-out, transform 0.2s ease-in-out;
      }
      button:hover {
        background-color: ${theme.palette.primary.dark};
        transform: scale(1.05);
      }
      button[aria-busy='true'] {
        cursor: progress;
        transform: none;
      }
    `}</style>
  </>
)
